import React, { useEffect, useState } from "react";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);

const MINUTES = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

export default function FuturisticTimePicker({
  value = "08:30 PM",
  onChange,
}) {
  const parseTime = (time) => {
    const [clock, period] = time.split(" ");
    const [hour, minute] = clock.split(":");

    return {
      hour: parseInt(hour),
      minute,
      period,
    };
  };

  const initial = parseTime(value);

  const [selectedHour, setSelectedHour] = useState(initial.hour);
  const [selectedMinute, setSelectedMinute] = useState(initial.minute);
  const [selectedPeriod, setSelectedPeriod] = useState(initial.period);

  useEffect(() => {
    const formatted = `${String(selectedHour).padStart(
      2,
      "0"
    )}:${selectedMinute} ${selectedPeriod}`;

    onChange?.(formatted);
  }, [selectedHour, selectedMinute, selectedPeriod]);

  return (
    <div
      className="
      tw-relative
      tw-w-full
      tw-max-w-[380px]
      tw-overflow-hidden
      tw-rounded-[32px]
      tw-border
      tw-border-white/10
      tw-bg-[#07111f]/90
      tw-p-6
      tw-backdrop-blur-xl
      tw-shadow-[0_0_60px_rgba(0,255,255,0.15)]
    "
    >
      {/* Glow Effects */}
      <div className="tw-absolute tw--left-20 tw-top-0 tw-h-40 tw-w-40 tw-rounded-full tw-bg-cyan-500/20 tw-blur-3xl" />

      <div className="tw-absolute tw--right-16 tw-bottom-0 tw-h-40 tw-w-40 tw-rounded-full tw-bg-purple-500/20 tw-blur-3xl" />

      {/* Header */}
      <div className="tw-relative tw-z-10 tw-flex tw-items-center tw-justify-between">
        <div>
          <h2 className="tw-text-2xl tw-font-bold tw-text-white">
            Time Picker
          </h2>

          <p className="tw-mt-1 tw-text-sm tw-text-cyan-300/70">
            Futuristic UI
          </p>
        </div>

        <div
          className="
          tw-flex
          tw-h-14
          tw-w-14
          tw-items-center
          tw-justify-center
          tw-rounded-2xl
          tw-border
          tw-border-cyan-400/20
          tw-bg-cyan-400/10
          tw-text-2xl
        "
        >
          ⏱
        </div>
      </div>

      {/* Time Display */}
      <div
        className="
        tw-relative
        tw-z-10
        tw-mt-6
        tw-rounded-3xl
        tw-border
        tw-border-white/10
        tw-bg-white/5
        tw-py-6
        tw-text-center
        tw-backdrop-blur-lg
      "
      >
        <div
          className="
          tw-text-5xl
          tw-font-black
          tw-tracking-[4px]
          tw-text-white
        "
        >
          <span>{String(selectedHour).padStart(2, "0")}</span>

          <span className="tw-text-cyan-300">:</span>

          <span>{selectedMinute}</span>
        </div>

        <div
          className="
          tw-mt-2
          tw-inline-flex
          tw-rounded-full
          tw-border
          tw-border-cyan-400/20
          tw-bg-cyan-400/10
          tw-px-4
          tw-py-1
          tw-text-sm
          tw-font-semibold
          tw-text-cyan-300
        "
        >
          {selectedPeriod}
        </div>
      </div>

      {/* Hour Selection */}
      <div className="tw-relative tw-z-10 tw-mt-8">
        <h3 className="tw-mb-4 tw-text-sm tw-font-semibold tw-text-white/70">
          HOURS
        </h3>

        <div className="tw-grid tw-grid-cols-4 tw-gap-3">
          {HOURS.map((hour) => {
            const active = selectedHour === hour;

            return (
              <button
                key={hour}
                onClick={() => setSelectedHour(hour)}
                className={`
                  tw-rounded-2xl
                  tw-border
                  tw-py-3
                  tw-text-sm
                  tw-font-semibold
                  tw-transition-all
                  tw-duration-300
                  hover:tw-scale-105
                  active:tw-scale-95

                  ${
                    active
                      ? `
                        tw-border-cyan-300/50
                        tw-bg-cyan-400/20
                        tw-text-cyan-300
                        tw-shadow-[0_0_20px_rgba(34,211,238,0.35)]
                      `
                      : `
                        tw-border-white/10
                        tw-bg-white/5
                        tw-text-white/80
                        hover:tw-bg-white/10
                      `
                  }
                `}
              >
                {String(hour).padStart(2, "0")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Minute Selection */}
      <div className="tw-relative tw-z-10 tw-mt-8">
        <h3 className="tw-mb-4 tw-text-sm tw-font-semibold tw-text-white/70">
          MINUTES
        </h3>

        <div
          className="
          tw-grid
          tw-max-h-[220px]
          tw-grid-cols-4
          tw-gap-3
          tw-overflow-y-auto
          tw-pr-1
        "
        >
          {MINUTES.map((minute) => {
            const active = selectedMinute === minute;

            return (
              <button
                key={minute}
                onClick={() => setSelectedMinute(minute)}
                className={`
                  tw-rounded-2xl
                  tw-border
                  tw-py-3
                  tw-text-sm
                  tw-font-semibold
                  tw-transition-all
                  tw-duration-300
                  hover:tw-scale-105
                  active:tw-scale-95

                  ${
                    active
                      ? `
                        tw-border-purple-300/50
                        tw-bg-purple-400/20
                        tw-text-purple-200
                        tw-shadow-[0_0_20px_rgba(168,85,247,0.35)]
                      `
                      : `
                        tw-border-white/10
                        tw-bg-white/5
                        tw-text-white/80
                        hover:tw-bg-white/10
                      `
                  }
                `}
              >
                {minute}
              </button>
            );
          })}
        </div>
      </div>

      {/* AM PM */}
      <div className="tw-relative tw-z-10 tw-mt-8 tw-grid tw-grid-cols-2 tw-gap-4">
        {["AM", "PM"].map((period) => {
          const active = selectedPeriod === period;

          return (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`
                tw-rounded-2xl
                tw-border
                tw-py-4
                tw-font-bold
                tw-transition-all
                tw-duration-300
                hover:tw-scale-[1.02]
                active:tw-scale-95

                ${
                  active
                    ? `
                      tw-border-cyan-300/50
                      tw-bg-gradient-to-r
                      tw-from-cyan-500/30
                      tw-to-purple-500/30
                      tw-text-white
                      tw-shadow-[0_0_25px_rgba(34,211,238,0.25)]
                    `
                    : `
                      tw-border-white/10
                      tw-bg-white/5
                      tw-text-white/70
                    `
                }
              `}
            >
              {period}
            </button>
          );
        })}
      </div>
    </div>
  );
}